"use client";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

export interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  syncWithHash?: boolean;
}

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(
  (
    { syncWithHash = true, value, defaultValue, onValueChange, ...props },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const [hashValue, setHashValue] = React.useState<string | undefined>(
      undefined,
    );
    const [mounted, setMounted] = React.useState(false);
    // Ref to track if we're internally updating the hash to prevent infinite loops
    const isInternalHashUpdateRef = React.useRef(false);

    // After mount, read hash and update state
    React.useEffect(() => {
      setMounted(true);
      if (syncWithHash && typeof window !== "undefined") {
        const hash = window.location.hash.slice(1);
        if (hash) {
          setHashValue(hash);
        }
      }
    }, [syncWithHash]);

    // When syncWithHash is enabled and not controlled, use controlled mode with hash/defaultValue
    // This ensures we can update the value after mount based on hash
    const shouldUseControlled = syncWithHash ? true : isControlled;

    // Determine the value to use:
    // - If controlled (value prop provided), use that
    // - If syncWithHash: use defaultValue initially (server and client match), then hash after mount
    // - Otherwise, use undefined (uncontrolled mode)
    const tabValue = isControlled
      ? value
      : syncWithHash
        ? mounted && hashValue
          ? hashValue
          : defaultValue
        : undefined;

    // Listen for hash changes
    React.useEffect(() => {
      if (!syncWithHash || !mounted) return;

      const handleHashChange = () => {
        // Ignore hashchange events that we triggered internally
        if (isInternalHashUpdateRef.current) {
          isInternalHashUpdateRef.current = false;
          return;
        }
        const hash = window.location.hash.slice(1);
        setHashValue(hash || undefined);
      };

      window.addEventListener("hashchange", handleHashChange);
      return () => window.removeEventListener("hashchange", handleHashChange);
    }, [syncWithHash, mounted]);

    // Sync hash when value changes externally (for controlled mode)
    React.useEffect(() => {
      if (
        syncWithHash &&
        mounted &&
        isControlled &&
        value &&
        typeof window !== "undefined"
      ) {
        const currentHash = window.location.hash.slice(1);
        if (currentHash !== value) {
          // Mark that we're internally updating the hash
          isInternalHashUpdateRef.current = true;
          window.location.hash = value;
          setHashValue(value);
        }
      }
    }, [syncWithHash, mounted, isControlled, value]);

    const handleValueChange = React.useCallback(
      (newValue: string) => {
        if (syncWithHash && typeof window !== "undefined") {
          // Only update hash if it's different to avoid unnecessary hashchange events
          const currentHash = window.location.hash.slice(1);
          if (currentHash !== newValue) {
            // Mark that we're internally updating the hash
            isInternalHashUpdateRef.current = true;
            window.location.hash = newValue;
            setHashValue(newValue);
          }
        }
        onValueChange?.(newValue);
      },
      [syncWithHash, onValueChange],
    );

    return (
      <TabsPrimitive.Root
        ref={ref}
        value={shouldUseControlled ? tabValue : undefined}
        defaultValue={shouldUseControlled ? undefined : defaultValue}
        onValueChange={handleValueChange}
        {...props}
      />
    );
  },
);
Tabs.displayName = TabsPrimitive.Root.displayName;

const tabsListVariants = cva(
  "flex items-center justify-center text-muted-foreground",
  {
    variants: {
      variant: {
        default: "rounded-xs bg-transparent border-2 border-stroke-light p-1",
        bordered: "bg-transparent gap-2",
        outlined: "bg-transparent gap-3",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant, className }))}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 font-semibold",
  {
    variants: {
      variant: {
        default:
          "w-full px-3 py-1 text-sm rounded-xs data-[state=active]:bg-bg-tertiary data-[state=active]:text-foreground data-[state=active]:shadow",
        bordered:
          "w-full px-3 py-1 text-sm data-[state=active]:border-b data-[state=active]:border-text-primary data-[state=active]:text-text-primary data-[state=inactive]:text-text-disabled",
        outlined:
          "px-2 py-1.5 text-base rounded-sm border-2 text-sm border-stroke-light text-text-secondary data-[state=active]:border-text-secondary data-[state=active]:text-text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      tabsTriggerVariants({ variant, className }),
      "cursor-pointer",
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  tabsListVariants,
  tabsTriggerVariants,
};
